"use client";

import { getAuthorBlogList } from "@/lib/api/client/author";
import { ErrorCode } from "@/lib/error/error";
import { useState } from "react";
import { IntlLink } from "@/lib/utils/navigation";
import { convertToWebp } from "@/lib/utils/image";
import { useTranslations } from "next-intl";

interface AuthorBlogListProps {
  author_id: string;
  author_name: string;
  initialBlogs: AUTHOR_API.GetAuthorBlogListReplyBlogInfo[];
  totalPages: number;
  currentPage?: number;
}

export default function AuthorBlogList(props: AuthorBlogListProps) {
  const t = useTranslations();
  const { author_id, author_name, initialBlogs, totalPages, currentPage = 1 } = props;
  const [loading, setLoading] = useState(false);
  const [blogList, setBlogList] = useState<AUTHOR_API.GetAuthorBlogListReplyBlogInfo[]>(initialBlogs);
  const [page, setPage] = useState(currentPage);

  const loadPage = async (pageNum: number) => {
    setLoading(true);
    try {
      const response = await getAuthorBlogList({
        author_id,
        page: pageNum,
        page_size: 10
      });

      if (response && response.code === ErrorCode.ErrSuccess) {
        setBlogList(response.metadata.blog_list);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Failed to load blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (blogList.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-y-[40px] mt-[32px]">
      <div style={{
        color: '#161615',
        fontSize: 30,
        fontWeight: 600,
        lineHeight: '1',
      }}>
        {t("author.title", { authorName: author_name })}
      </div>
      {blogList.map((blog) => (
        <IntlLink
          key={blog.blog_id}
          href={`/blog/${blog.url_blog_name}`}
          className="group"
        >
          <div className="flex gap-[24px] items-center">
            <div className="md:w-[200px] md:h-[120px] w-[140px] h-[100px] overflow-hidden rounded-[12px] flex-shrink-0">
              <img
                src={convertToWebp(blog.cover_url)}
                alt={blog.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              />
            </div>
            <div className="flex flex-col gap-[12px]">
              {/* 超过两行显示 ... */}
              <h3 className="md:text-[20px] text-[16px] font-semibold text-[#2A2A29] transition-colors" style={{
                lineHeight: "22px",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}>
                {blog.title}
              </h3>
              <div className="md:text-[16px] text-[14px] text-[#595856]">
                {blog.publish_date}
              </div>
            </div>
          </div>
        </IntlLink>
      ))}

      {/* Pagination */}
      {
        totalPages <= 1
          ? <div className="flex justify-center items-center mt-[32px] mb-[72px]">
            <div className="flex-1 max-w-[120px] h-[1px] bg-[#DAD8D3] rounded-full"></div>
            <div className="px-[24px] flex-shrink-0">
              <p className="text-[#ABAAA6] text-[16px] font-[400] whitespace-nowrap">
                {t("author.noMorePosts")}
              </p>
            </div>
            <div className="flex-1 max-w-[120px] h-[1px] bg-[#DAD8D3] rounded-full"></div>
          </div> :
          (
            <div className="flex justify-center items-center gap-[16px] mt-[48px] mb-[72px]">
              <button
                onClick={() => page > 1 && loadPage(page - 1)}
                disabled={page === 1 || loading}
                className="w-[32px] h-[32px] flex items-center justify-center rounded-full border border-[#E5E5E4] disabled:opacity-50"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10 12L6 8L10 4" stroke="#2A2A29" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => pageNum !== page && loadPage(pageNum)}
                  disabled={loading}
                  className={`w-[32px] h-[32px] flex items-center justify-center rounded-full
                ${pageNum === page
                      ? 'text-[#FF596C] border border-[#FF596C]'
                      : 'text-[#2A2A29] hover:border-[#FF596C] hover:border'
                    }`}
                >
                  {pageNum}
                </button>
              ))}

              <button
                onClick={() => page < totalPages && loadPage(page + 1)}
                disabled={page === totalPages || loading}
                className="w-[32px] h-[32px] flex items-center justify-center rounded-full border border-[#E5E5E4] disabled:opacity-50"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 12L10 8L6 4" stroke="#2A2A29" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          )
      }
    </div>
  );
}